import { LOG_TYPES, getTimelineMetaForCategory, isBehaviorIncidentEntry } from "../../constants/logTypeRegistry";
import { removeMoodSnapshotsFromEntries, replaceMoodSnapshotInEntries } from "../../services/timeline/moodSnapshotUtils";
import { buildOptimisticIncidentEntryBase, buildOptimisticMoodEntryBase } from "../timeline/timelineOptimisticEntryFactories";
import { formatTimeForSummary, mergeEntriesForChild, toEntryDate } from "./panelDashboardTimelineUtils";

const applyPanelDashboardOptimisticEntry = ({
  entry,
  allEntries,
  setAllEntries,
  setIncidents,
  setRecentEntries,
  setTimelineSummary,
}) => {
  if (!entry || !entry.childId) {
    return;
  }

  const entryTimestamp = toEntryDate(entry.timestamp);
  if (Number.isNaN(entryTimestamp.getTime())) {
    return;
  }

  if (entry.collection === 'incidents') {
    const incidentMeta = getTimelineMetaForCategory('behavior', { importantMoment: false });
    const optimisticIncident = buildOptimisticIncidentEntryBase({
      entry,
      timestamp: entryTimestamp,
      title: entry.title || entry.content || 'Incident',
      content: entry.content || entry.notes || entry.triggerSummary || '',
      triggerSummary: entry.triggerSummary || entry.remedy || '',
      icon: entry.icon || incidentMeta.icon,
      color: entry.color || incidentMeta.color,
      incidentData: entry.incidentData && typeof entry.incidentData === 'object' && Object.keys(entry.incidentData).length > 0
        ? entry.incidentData
        : null,
      extra: {
        author: entry.authorName || entry.author || entry.loggedByUser || entry.authorEmail || 'Unknown',
        loggedByUser: entry.authorName || entry.author || entry.loggedByUser || entry.authorEmail || 'Unknown',
        userRole: entry.authorRole || null,
        userId: entry.authorId || entry.createdBy || null,
      },
    });

    setIncidents((current) => ({
      ...current,
      [entry.childId]: [
        optimisticIncident,
        ...((current[entry.childId] || []).filter((item) => item.id !== optimisticIncident.id)),
      ],
    }));
    setAllEntries((current) => {
      const currentEntries = current[entry.childId] || [];
      const nextEntries = mergeEntriesForChild([optimisticIncident], currentEntries);

      return {
        ...current,
        [entry.childId]: nextEntries,
      };
    });
    return;
  }

  if (entry.collection !== 'dailyLogs' && entry.collection !== 'dailyCare') {
    return;
  }

  const isDeletedMoodEntry = entry.collection === 'dailyCare'
    && entry.actionType === 'mood'
    && entry.status === 'deleted';
  if (isDeletedMoodEntry) {
    const moodRemovalEntry = {
      ...entry,
      type: 'mood',
      actionType: 'mood',
    };
    setRecentEntries((current) => {
      const currentEntries = current[entry.childId] || [];
      return {
        ...current,
        [entry.childId]: removeMoodSnapshotsFromEntries(currentEntries, moodRemovalEntry),
      };
    });
    setAllEntries((current) => {
      const currentEntries = current[entry.childId] || [];
      return {
        ...current,
        [entry.childId]: removeMoodSnapshotsFromEntries(currentEntries, moodRemovalEntry),
      };
    });
    return;
  }

  const meta = getTimelineMetaForCategory(entry.category || entry.logCategory || null, { importantMoment: !!entry.importantMoment });
  const isBehaviorStyleEntry = isBehaviorIncidentEntry(entry);
  const isMoodEntry = entry.collection === 'dailyCare' && entry.actionType === 'mood';
  const notesText = entry.notes || entry.incidentData?.notes || null;
  const severityLabel = entry.severityLabel || entry.incidentData?.severityLabel || null;
  const triggerSummary = entry.triggerSummary || entry.incidentData?.triggerSummary || null;
  const moodValue = entry.value || entry.data?.level || entry.mood || entry.moodLevel || entry.content || entry.text || 'Calm';

  if (isMoodEntry) {
    const optimisticMoodEntry = buildOptimisticMoodEntryBase({
      entry,
      timestamp: entryTimestamp,
      moodValue,
      notes: notesText,
      contextSnapshot: entry.contextSnapshot || entry.incidentData?.contextSnapshot || null,
      incidentData: entry.incidentData && typeof entry.incidentData === 'object' && Object.keys(entry.incidentData).length > 0
        ? entry.incidentData
        : null,
      severityLabel,
      triggerSummary,
      remedy: entry.remedy || entry.incidentData?.remedy || null,
      extra: {
        logCategory: entry.logCategory || entry.category || meta.category || meta.type,
        label: 'Mood',
        icon: LOG_TYPES.mood.icon,
        color: LOG_TYPES.mood.palette.dot,
        author: entry.authorName || entry.author || entry.loggedByUser || entry.authorEmail || 'Unknown',
        loggedByUser: entry.authorName || entry.author || entry.loggedByUser || entry.authorEmail || 'Unknown',
        userRole: entry.authorRole || null,
        userId: entry.authorId || entry.createdBy || null,
        incidentStyle: false,
        entryType: 'mood',
        incidentCategoryId: 'mood',
        incidentCategoryLabel: 'Mood',
        incidentCategoryColor: LOG_TYPES.mood.palette.dot,
        incidentCategoryIcon: LOG_TYPES.mood.icon,
        mood: String(moodValue),
        moodEmoji: LOG_TYPES.mood.icon,
      },
    });

    setRecentEntries((current) => {
      const currentEntries = current[entry.childId] || [];
      const nextEntries = replaceMoodSnapshotInEntries(currentEntries, optimisticMoodEntry).slice(0, 5);

      return {
        ...current,
        [entry.childId]: nextEntries,
      };
    });

    setAllEntries((current) => {
      const currentEntries = current[entry.childId] || [];
      const nextEntries = replaceMoodSnapshotInEntries(currentEntries, optimisticMoodEntry);

      return {
        ...current,
        [entry.childId]: nextEntries,
      };
    });

    setTimelineSummary((current) => {
      const childSummary = current[entry.childId] || {};
      const currentEntries = allEntries[entry.childId] || [];
      const nextEntries = replaceMoodSnapshotInEntries(currentEntries, optimisticMoodEntry);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todaysEntries = nextEntries.filter((item) => {
        const dayEntryDate = toEntryDate(item.timestamp);
        return !Number.isNaN(dayEntryDate.getTime()) && dayEntryDate >= todayStart;
      });

      return {
        ...current,
        [entry.childId]: {
          ...childSummary,
          totalEntries: nextEntries.length,
          todayCount: todaysEntries.length,
          weekCount: nextEntries.length,
          activityStreak: Math.max(childSummary.activityStreak || 0, 1),
          lastActivityTime: formatTimeForSummary(optimisticMoodEntry),
        },
      };
    });
    return;
  }

  const optimisticEntry = {
    id: entry.id || `local-${Date.now()}`,
    childId: entry.childId,
    logCategory: entry.logCategory || entry.category || meta.category || meta.type,
    type: isBehaviorStyleEntry ? 'behavior' : meta.type,
    timelineType: isBehaviorStyleEntry ? 'incident' : meta.type,
    collection: entry.collection || 'dailyLogs',
    category: entry.category || 'log',
    actionType: entry.actionType || null,
    title: entry.title || entry.titlePrefix || entry.text || meta.label,
    titlePrefix: entry.titlePrefix,
    content: entry.content || entry.text || entry.incidentData?.notes || '',
    moodValue: entry.moodValue,
    label: entry.label,
    text: entry.text || entry.content || '',
    notes: notesText,
    timestamp: entryTimestamp,
    localTime: entry.localTime || entry.time || null,
    time: entry.time || entry.localTime || null,
    timeSource: entry.timeSource || null,
    timestampSource: entry.timestampSource || null,
    parserMode: entry.parserMode || null,
    sourceMode: entry.sourceMode || null,
    metadata: entry.metadata || null,
    icon: isBehaviorStyleEntry ? LOG_TYPES.behavior.icon : meta.icon,
    color: isBehaviorStyleEntry ? LOG_TYPES.behavior.palette.dot : meta.color,
    tags: entry.tags || [],
    importantMoment: !!entry.importantMoment,
    author: entry.authorName || entry.author || entry.loggedByUser || entry.authorEmail || 'Unknown',
    loggedByUser: entry.authorName || entry.author || entry.loggedByUser || entry.authorEmail || 'Unknown',
    userRole: entry.authorRole || null,
    userId: entry.authorId || entry.createdBy || null,
    incidentStyle: isBehaviorStyleEntry,
    entryType: isBehaviorStyleEntry ? 'incident' : entry.entryType,
    contextSnapshot: entry.contextSnapshot || entry.incidentData?.contextSnapshot || null,
    incidentData: entry.incidentData && typeof entry.incidentData === 'object' && Object.keys(entry.incidentData).length > 0
      ? entry.incidentData
      : null,
    severity: entry.severity,
    severityLabel,
    triggerSummary,
    remedy: entry.remedy || entry.incidentData?.remedy || null,
    incidentCategoryId: isBehaviorStyleEntry ? 'behavior' : entry.incidentCategoryId,
    incidentCategoryLabel: isBehaviorStyleEntry ? 'Behavior' : entry.incidentCategoryLabel,
    incidentCategoryColor: isBehaviorStyleEntry ? LOG_TYPES.behavior.palette.dot : entry.incidentCategoryColor,
    incidentCategoryIcon: isBehaviorStyleEntry ? undefined : entry.incidentCategoryIcon,
    activityDetails: entry.activityDetails || null,
  };

  setRecentEntries((current) => {
    const currentEntries = current[entry.childId] || [];
    const nextEntries = [
      optimisticEntry,
      ...currentEntries.filter((item) => item.id !== optimisticEntry.id),
    ]
      .sort((a, b) => toEntryDate(b.timestamp) - toEntryDate(a.timestamp))
      .slice(0, 5);

    return {
      ...current,
      [entry.childId]: nextEntries,
    };
  });

  setAllEntries((current) => {
    const currentEntries = current[entry.childId] || [];
    const nextEntries = [
      optimisticEntry,
      ...currentEntries.filter((item) => item.id !== optimisticEntry.id),
    ].sort((a, b) => toEntryDate(b.timestamp) - toEntryDate(a.timestamp));

    return {
      ...current,
      [entry.childId]: nextEntries,
    };
  });

  setTimelineSummary((current) => {
    const childSummary = current[entry.childId] || {};
    const childEntries = (allEntries[entry.childId] || []).length > 0
      ? [optimisticEntry, ...(allEntries[entry.childId] || []).filter((item) => item.id !== optimisticEntry.id)]
      : [optimisticEntry];
    const todaysEntries = childEntries.filter((item) => {
      const dayEntryDate = toEntryDate(item.timestamp);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      return !Number.isNaN(dayEntryDate.getTime()) && dayEntryDate >= todayStart;
    });

    return {
      ...current,
      [entry.childId]: {
        ...childSummary,
        totalEntries: Math.max(childSummary.totalEntries || 0, todaysEntries.length),
        todayCount: Math.max(childSummary.todayCount || 0, todaysEntries.length),
        weekCount: Math.max(childSummary.weekCount || 0, childEntries.length),
        activityStreak: Math.max(childSummary.activityStreak || 0, 1),
        lastActivityTime: formatTimeForSummary(optimisticEntry),
      },
    };
  });
};

const applyPanelDashboardOptimisticEdit = ({
  entry,
  setAllEntries,
  setRecentEntries,
}) => {
  if (!entry?.id || !entry?.childId) {
    return;
  }

  const mergeEditedEntry = (currentEntry) => {
    if (currentEntry?.id !== entry.id) {
      return currentEntry;
    }

    const nextTimestamp = entry.timestamp ? toEntryDate(entry.timestamp) : currentEntry.timestamp;
    const nextText = entry.body || entry.text || entry.content || currentEntry.body || currentEntry.text || currentEntry.content || '';

    return {
      ...currentEntry,
      ...entry,
      timestamp: Number.isNaN(nextTimestamp?.getTime?.()) ? currentEntry.timestamp : nextTimestamp,
      text: nextText,
      body: nextText,
      content: nextText,
      note: nextText,
      notes: nextText,
      time: entry.time ?? currentEntry.time ?? null,
      localTime: entry.localTime ?? currentEntry.localTime ?? null,
      timeSource: entry.timeSource ?? currentEntry.timeSource,
      timeBucket: entry.timeBucket ?? currentEntry.timeBucket ?? null,
      timeInferred: entry.timeInferred ?? currentEntry.timeInferred ?? false,
      timeInferenceReason: entry.timeInferenceReason ?? currentEntry.timeInferenceReason ?? null,
      editedAt: entry.editedAt || currentEntry.editedAt || new Date(),
      editState: entry.editState || currentEntry.editState || 'edited',
      incidentData: currentEntry.incidentData
        ? {
            ...currentEntry.incidentData,
            description: nextText,
            notes: nextText,
          }
        : currentEntry.incidentData,
    };
  };

  const patchEntriesForChild = (current) => {
    const childEntries = current[entry.childId] || [];
    if (!childEntries.some((item) => item.id === entry.id)) {
      return current;
    }

    return {
      ...current,
      [entry.childId]: childEntries
        .map(mergeEditedEntry)
        .sort((a, b) => toEntryDate(b.timestamp) - toEntryDate(a.timestamp)),
    };
  };

  setAllEntries(patchEntriesForChild);
  setRecentEntries((current) => {
    const patched = patchEntriesForChild(current);
    const childEntries = patched[entry.childId];
    if (!childEntries) {
      return patched;
    }

    return {
      ...patched,
      [entry.childId]: childEntries.slice(0, 5),
    };
  });
};

export {
  applyPanelDashboardOptimisticEdit,
  applyPanelDashboardOptimisticEntry,
};
